import { HttpRequest } from "../../Pages/httpRequest";
import { IFacetArea, ICheckedFacet } from "./facet-interfaces";

export class Facets {
    private displayOrder: HTMLSelectElement = document.getElementById("DisplayOrder") as HTMLSelectElement;
    private expandedFacetGroupId: number;
    private facetsContainer = document.getElementById("facetTab") as HTMLDivElement;
    private facetIoCounter: number = 0; // Prevents out of order server responses, only the most recent is used.
    private categoryId: number;
    private outerProductContainer: HTMLElement = document.getElementById('inner-product-container');
    private innerProductContainer: HTMLElement = document.getElementById('category-image-scroller');
    private loadMoreButton: HTMLElement = document.getElementById("loadMoreButton");
    private loadPreviousButton: HTMLElement = document.getElementById("loadPreviousButton");
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;
    private productsViewed: HTMLElement = document.getElementById("productsViewed");
    private productsViewedProgress: HTMLElement = document.getElementById("productsViewedProgress");
    private viewAllButton: HTMLElement = document.getElementById("viewAllButton");

    private maxPageShown: number;
    private minPageShown: number;
    private showAll: boolean = false; // Show all products in the main UI.

    /// <summary>
    /// The number of facets to show when the 'more options' link is clicked.
    /// Also defined in the server side FacetFilter class.
    /// </summary>
    private readonly MoreOptionsShowCount = 10;

    constructor() {
        if (this.facetsContainer) {
            this.categoryId = parseInt((<HTMLInputElement>document.getElementById("CategoryId")).value);
            this.maxPageShown = parseInt((<HTMLInputElement>document.getElementById("MaxPageShown")).value);;
            this.minPageShown = parseInt((<HTMLInputElement>document.getElementById("MinPageShown")).value);;

            this.wireUpCheckBoxListeners();
            this.wireUpPager();
            this.wireUpClickHandlers();
            this.wireUpSearchHandlers();
        }
    }

    public closeMobileFilter(e: Event): void {
        e.preventDefault();
        document.documentElement.classList.remove("show-filter");
    }

    private wireUpSearchHandlers(): void {
        // Use event delegation on the root facet element, listening for input entry on descendents.
        this.facetsContainer.addEventListener("input", (e) => this.handleFacetSearch(e));
    }

    private handleFacetSearch(e: Event): void {
        if (e.target instanceof HTMLInputElement) {
            if (e.target.parentElement.classList.contains("filter_search")) {
                e.preventDefault();

                // Find the facet group id from the chevron link in the same parent LI.
                let li = e.target.closest("li");
                let isChevronExpandedElement = li.querySelector<HTMLInputElement>(".is-chevron-expanded");
                let facetGroupId = parseInt(isChevronExpandedElement.value);
                let groupParentUl = document.getElementById(`facet-group-${facetGroupId}`);
                let searchText = e.target.value.toLowerCase();

                // Look in the parent UL for the list items to filter. The li will contain a label which contains multiple spans with the text to search.
                let lis = groupParentUl.querySelectorAll("li");

                // As we are searching, show all of the facet items in the group.
                let loadMoreElement = isChevronExpandedElement.parentElement.querySelector<HTMLSpanElement>("span.facetoptions");
                this.showAllGroupItems(loadMoreElement);

                lis.forEach(li => {
                    let label = li.querySelector("label");
                    let spans = label.querySelectorAll("span");
                    let found = false;

                    spans.forEach(span => {
                        if (span.textContent.toLowerCase().includes(searchText)) {
                            found = true;
                        }
                    });

                    if (found) {
                        li.classList.remove("hidden");
                    }
                    else {
                        li.classList.add("hidden");
                    }
                });
            }
        }
    }

    /**
     * Captures the clicks on the tag summary (for mobile) so that when clicked, they untick the related facet
     * item which will cause a reload and the summary to be updated without the tag just clicked.
     * 
     * For desktop listens for the clicks on 'load more' spans.
     */
    private wireUpClickHandlers(): void {
        // Use event delegation on the root facet element, listening for clicks on descendents.
        this.facetsContainer.addEventListener("click", (e) => this.handleFacetAreaClick(e));
    }

    /**
     * Routes the click events in the facet area to the correct handler depending on the
     * element clicked.
     */
    private handleFacetAreaClick(e: MouseEvent): boolean {
        if (e.target instanceof HTMLAnchorElement) {
            e.preventDefault();

            if (e.target.classList.contains("clear-filters")) {
                // The click event was for the overall 'clear everything' link on mobile.
                this.onClearAllClicked(e.target);
            }
            else if (e.target.classList.contains("filterToggleText")) {
                // The click event was for a facet group title row such as stock, size colour etc.
                this.onFacetGroupNameClicked(e.target);
            }
            else if (e.target.parentElement.classList.contains("back")) {
                // The click event was for the back button on mobile.
                this.onFacetGroupNameClicked(e.target);
            }
        }
        if (e.target instanceof HTMLSpanElement) {
            if (e.target.classList.contains("facet-group")) {
                e.preventDefault();
                // The click was for 'more options' on a facet group.
                this.onMoreGroupOptionsClicked(e.target);
            }
            else if (e.target.classList.contains("facet-list")) {
                e.preventDefault();
                // The click was for 'more options' on a list below a facet group.
                this.onMoreItemOptionsClicked(e.target);
            }
            else if (e.target.parentElement.classList.contains("view-facet-products")) {
                // The click event was for the 'view xx results' link on mobile.
                this.closeMobileFilter(e);
            }
        }

        return false;
    }

    /**
     * Handles a click on the 'clear filters' text link to clear all groups, unticks
     * all of the facets and then reloads the UI.
     * @param clickedAnchor : The anchor element that is the 'clear filters' link.
     */
    private onClearAllClicked(clickedAnchor: HTMLAnchorElement): void {
        // Find all facet checkboxes.
        const checkboxes = this.facetsContainer
            .querySelectorAll<HTMLInputElement>(`input[type="checkbox"][data-facet-group-id]`);

        // Untick all of the checkboxes.
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
        });

        // Put the default 'in stock' back on.
        let inStockCheckbox = this.facetsContainer
            .querySelector<HTMLInputElement>(`input[type="checkbox"][data-facet-group-id="7"][value="In Stock"]`);
        inStockCheckbox.checked = true;

        // Then request a page reload which will update the filtered products and facets.
        this.maxPageShown = 1;
        this.minPageShown = 1;
        this.requestFilteredProducts(0, 1);
    }

    /**
     * On mobile the facet group name (stock, colour, size etc) is clickable and both opens
     * and closes the facet.
     * @param clickedAnchor : The a tag that was clicked, can be the title or a specific back link.
     */
    private onFacetGroupNameClicked(clickedAnchor: HTMLAnchorElement): void {
        let li = clickedAnchor.closest("li");
        let div = li.querySelector("div");

        // Toggle the chevron indicator.
        li.classList.toggle("active_parent");

        // Toggle the display of the div containing the tickable facet items.
        div.classList.toggle("active");

        // Find the group id that has been clicked so we can track it as expanded.
        const expandedCountElement = li.querySelector<HTMLInputElement>(".expanded-group-count");
        this.expandedFacetGroupId = parseInt(expandedCountElement.id.replace("expanded-group-count-", ""));

        const isChevronExpandedElement = li.querySelector<HTMLInputElement>(".is-chevron-expanded");
        isChevronExpandedElement.checked = !isChevronExpandedElement.checked;

        // Find the 'more options' span and click that as it'll keep track of the expanded group.
        let span = li.querySelector<HTMLSpanElement>("span.facetoptions");

        if (span) { // 'More options' might not exist for all groups.
            let event = new Event("click", { 'bubbles': true, 'cancelable': true });
            span.dispatchEvent(event);
        }
    }

    private wireUpPager(): void {
        this.loadMoreButton.addEventListener("click", (e) => this.onLoadMoreClicked(e));
        this.loadPreviousButton.addEventListener("click", (e) => this.onLoadPreviousClicked(e));
        this.viewAllButton.addEventListener("click", (e) => this.onViewAllClicked(e));
        this.watchForLoadMoreButton();
        this.displayOrder.addEventListener("change", (e) => this.onDisplayOrderChanged(e));
    }

    private onDisplayOrderChanged(e: Event): void {
        this.maxPageShown = 1;
        this.minPageShown = 1;
        this.requestFilteredProducts(0, 1);
    }

    private onViewAllClicked(e: MouseEvent): boolean {
        e.preventDefault();
        this.showAll = true;
        this.maxPageShown = 1;
        this.minPageShown = 1;
        this.requestFilteredProducts(0, 1);
        return false;
    }

    private onLoadMoreClicked(e: MouseEvent): boolean {
        e.preventDefault();
        this.requestFilteredProducts(1, this.maxPageShown + 1);
        return false;
    }

    private onLoadPreviousClicked(e: MouseEvent): boolean {
        e.preventDefault();
        this.requestFilteredProducts(-1, this.minPageShown - 1);
        return false;
    }

    private wireUpCheckBoxListeners(): void {
        // Use event delegation on the root facet element, listening for clicks on descendents.
        this.facetsContainer.addEventListener("change", (e) => this.facetItemChanged(e));
    }

    private facetItemChanged(e: Event): void {
        let checkbox = e.target as HTMLInputElement;

        // Make sure the clicked element is a checkbox.
        if (checkbox.matches('input[type="checkbox"]')) {
            this.maxPageShown = 1;
            this.minPageShown = 1;
            this.requestFilteredProducts(0, 1);
        }
    }

    /**
     * Shows more facet items (colour, size, product type etc.) for the facet group that the link
     * was clicked from.
     * 
     * Does not need to refetch the facet data for toggling facet item visibility as all facets items are loaded on the client.
     * @param clickedAnchor
     */
    private onMoreGroupOptionsClicked(loadMoreElement: HTMLSpanElement): void {
        this.showGroupItems(loadMoreElement, this.MoreOptionsShowCount);
    }

    private showAllGroupItems(loadMoreElement: HTMLSpanElement): void {
        this.showGroupItems(loadMoreElement, 999);
    }

    private showGroupItems(loadMoreElement: HTMLSpanElement, maxVisible: number): void {
        const expandedCountElement = loadMoreElement.parentElement.querySelector<HTMLInputElement>(".expanded-group-count");

        if (expandedCountElement) {
            let facetGroupId = parseInt(expandedCountElement.id.replace("expanded-group-count-", ""));
            let groupParentUl = document.getElementById(`facet-group-${facetGroupId}`);

            if (groupParentUl) {
                const liElements = groupParentUl.children;
                const expandFrom = parseInt(expandedCountElement.value);

                // Expand up to 10 items or the end of the list if closer.
                let expandTo = Math.min(expandFrom + maxVisible, liElements.length);

                if (expandFrom === expandTo) {
                    // All items are already visible, so this is a 'show less' click. Show just the first n items.
                    expandTo = 5;
                    for (let i = 0; i < liElements.length; i++) {
                        if (i < expandTo) {
                            liElements[i].classList.remove("hidden");
                        }
                        else {
                            liElements[i].classList.add("hidden");
                        }
                    }
                }
                else {
                    for (let i = expandFrom; i < expandTo; i++) {
                        liElements[i].classList.remove("hidden");
                    }
                }

                // Save the number of elements expanded back so that it gets included in any posts.
                expandedCountElement.value = expandTo.toString();

                if (expandTo >= liElements.length) {
                    loadMoreElement.textContent = "Show less";
                }
                else {
                    const nextExpandTo = Math.min(maxVisible, liElements.length - expandTo);
                    loadMoreElement.textContent = `Show ${nextExpandTo} more`;
                }
            }
        }
    }

    private onMoreItemOptionsClicked(loadMoreElement: HTMLSpanElement): void {
        const expandedCountElement = loadMoreElement.parentElement.querySelector<HTMLInputElement>(".expanded-list-count");

        if (expandedCountElement) {
            // Find the containing li.
            let parentLi = expandedCountElement.closest("li");
            // From that, find the UL which contains the items to expand.
            let groupParentUl = parentLi.querySelector(".facet-list-head");

            if (groupParentUl) {
                const liElements = groupParentUl.children;
                const expandFrom = parseInt(expandedCountElement.value);

                // Expand up to 10 items or the end of the list if closer.
                const expandTo = Math.min(expandFrom + 10, liElements.length);

                for (let i = expandFrom; i < expandTo; i++) {
                    liElements[i].classList.remove("hidden");
                }

                // Save the number of elements expanded back so that it gets included in any posts.
                expandedCountElement.value = expandTo.toString();

                if (expandTo >= liElements.length) {
                    loadMoreElement.classList.add("hidden");
                }
            }
        }
    }

    private requestFilteredProducts(pagingDirection: number, page: number): void {
        this.facetIoCounter++;
        const expectedIoCounter = this.facetIoCounter;

        this.outerProductContainer.classList.remove("fade-in");
        this.outerProductContainer.classList.add("fade-out");

        let data = {
            categoryId: this.categoryId,
            checkedFacets: this.checkedFacets(),
            displayOrder: this.displayOrder.value,
            expandedGroupCounts: this.expandedGroupCounts(),
            expandedMobileGroup: this.expandedFacetGroupId,
            expandedChevronGroups: this.expandedChevronGroups(),
            groupSearches: this.groupSearches(),
            maxPageShown: this.maxPageShown,
            minPageShown: this.minPageShown,
            page: page,
            showAll: this.showAll
        };
        let formData = JSON.stringify(data);

        let url = `${this.localeUrlPrefix}/ws/facet-filter`;
        HttpRequest.PostJson(url, formData, (json:string) => { this.rebind(json, pagingDirection, expectedIoCounter) }, null);
    }

    /**
     * Captures search text that has been entered on the client for each group so the
     * server view can set the value of the text boxes when rendering server side.
     * @returns: A dictionary of the group id and the search text.
     */
    private groupSearches(): Record<number, string> {
        const groupSearches: Record<number, string> = {};
        const inputs = document
            .querySelectorAll<HTMLInputElement>("div.filter_search>input");

        inputs.forEach(input => {
            if (input.value.trim().length > 0) {
                let facetGroupId: number = Number(input.dataset.facetGroupId);
                groupSearches[facetGroupId] = input.value;
            }
        });

        return groupSearches;
    }

    /**
     * Retrieves a list of the facet group ids that are expanded via the chevron next to their name.
     * Used to send to the server so that the server side view can render the items
     * expanded to the same point.
     * @returns
     */
    private expandedChevronGroups(): number[] {
        const inputs = document.querySelectorAll<HTMLInputElement>("input.is-chevron-expanded:checked");
        const expandedGroups: number[] = [];

        inputs.forEach(input => {
            const groupId = parseInt(input.value);
            expandedGroups.push(groupId);
        });

        return expandedGroups;
    }

    /**
     * Retrieves a list of the number of items in each facet group that are expanded.
     * Used to send to the server so that the server side view can render the items
     * expanded to the same point.
     * @returns
     */
    private expandedGroupCounts(): Record<number, number> {
        const groupCounts: Record<number, number> = {};
        const inputs = document.querySelectorAll<HTMLInputElement>("input.expanded-group-count");

        inputs.forEach(input => {
            // Remove "expanded-group-count-" from the id to use as the key.
            const key = parseInt(input.id.replace("expanded-group-count-", ""));
            // This is the count of the expanded items in this group.
            groupCounts[key] = parseInt(input.value);
        });

        return groupCounts;
    }

    private rebind(json: string, pagingDirection: number, expectedIoCounter: number) {
        if (this.facetIoCounter !== expectedIoCounter) {
            return;
        }

        let data = <IFacetArea>JSON.parse(json);

        this.outerProductContainer.classList.remove("fade-out");
        this.outerProductContainer.classList.add("fade-in");

        // Change the URL in the browser window so the user can copy and page through it.
        if (history.replaceState) {
            let url = window.location.protocol
                + "//"
                + window.location.host
                + window.location.pathname;
            if (data.queryString) {
                url = url + '?' + data.queryString;
            }
            history.replaceState(null, document.title, url);
        }
        else {
            window.location.hash = data.queryString;
        }

        this.maxPageShown = data.maxPageShown;
        this.minPageShown = data.minPageShown;
        this.productsViewed.textContent = data.productsViewedText;
        this.productsViewedProgress.style.width = data.productsViewedPercent;
        this.showAll = data.showAll;
        this.viewAllButton.textContent = data.viewAllButtonText;

        if (data.previousPageAvailable) {
            this.loadPreviousButton.classList.remove("hidden");
        }
        else {
            this.loadPreviousButton.classList.add("hidden");
        }

        if (data.nextPageAvailable) {
            this.loadMoreButton.classList.remove("hidden");
        }
        else {
            this.loadMoreButton.classList.add("hidden");
        }

        if (data.displayViewAllButton) {
            this.viewAllButton.classList.remove("hidden");
        }
        else {
            this.viewAllButton.classList.add("hidden");
        }

        // Set the tick indicator on the mobile 'Filter by' button.
        let filterMobile = document.querySelector(".filter-by-mb");
        if (filterMobile) {
            if (data.filtersActive) {
                filterMobile.classList.add("active");
            }
            else {
                filterMobile.classList.remove("active");
            }
        }

        // Replace the facets UI with what the server says is the current state.
        this.facetsContainer.innerHTML = data.facetUi;

        // On initial load the HTML will have been populated by the server and will be empty, so skip.
        if (pagingDirection === 0) {
            this.innerProductContainer.innerHTML = data.html;
        }
        else if (pagingDirection === 1) {
            this.innerProductContainer.insertAdjacentHTML('beforeend', data.html);
        }
        else {
            this.innerProductContainer.insertAdjacentHTML('afterbegin', data.html);
        }
    }

    private checkedFacets(): ICheckedFacet[] {
        const checkboxes = this.facetsContainer.querySelectorAll('input[type="checkbox"]:checked');

        return Array
            .from(checkboxes)
            .map((checkbox: HTMLInputElement) => ({ encodedName: checkbox.value, facetGroupId: checkbox.dataset.facetGroupId }));
    }

    private watchForLoadMoreButton(): void {
        const loadMoreButton = document.getElementById("loadMoreButton");

        let autoLoadCount = 0;

        // Watches for the load more button coming into view and clicks it when it does.
        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Automatically click the button when it comes into view
                    loadMoreButton.click();

                    // Only auto clicks the button to load up to 4 pages to avoid server hammering.
                    if (++autoLoadCount >= 3) {
                        observer.unobserve(loadMoreButton);
                    }
                }
            });
        });

        observer.observe(loadMoreButton);
    }
}
